import Nudge from '../Models/Nudge';

export default function getNudges(screen?: string): any {
  return null;
}

interface NudgeValidityCondition {
  validityType: string;
  parameters?: {
    range?: string;
    startRange?: string | number;
    endRange?: string | number;
    lastDate?: string;
    dependentNudges?: string[];
  };
}

interface NudgeMeta {
  [key: string]: any;
}

interface ValidatableNudge {
  campaignEnum: string;
  meta?: NudgeMeta;
  [key: string]: any;
}

export const isNudgeValid = (nudge: ValidatableNudge, currentDate: Date = new Date(), nudges: ValidatableNudge[]): boolean => {

  const nudgeValidity = nudge.meta?.nudgeValidity;
  if (!nudgeValidity || !Array.isArray(nudgeValidity)) {
    return true;
  }

  for (const condition of nudgeValidity) {
    if (!checkValidityCondition(condition, currentDate, nudges)) {
      return false;
    }
  }

  return true;
};

const checkValidityCondition = (condition: NudgeValidityCondition, currentDate: Date, nudges: ValidatableNudge[]): boolean => {
  const { validityType, parameters } = condition;

  switch (validityType) {
    case 'Default':
      return true;

    case 'FIXED_PERIOD':
      return checkFixedPeriod(parameters, currentDate);

    case 'MONTHLY_DATE_RANGE':
      return checkMonthlyDateRange(parameters, currentDate);

    case 'DEPENDENT_NUDGE_VALIDITY':
      return checkDependentNudgeValidity(parameters,nudges);

    case 'REFRESH_BASED':
      // Assuming REFRESH_BASED is always valid unless specific logic is needed
      return true;

    default:
      console.warn(`Unknown validity type: ${validityType}`);
      return true;
  }
};

const checkFixedPeriod = (parameters: any, currentDate: Date): boolean => {
  if (!parameters?.lastDate) {
    return true;
  }

  const lastDate = new Date(parameters.lastDate);
  if (isNaN(lastDate.getTime())) {
    console.warn(`Invalid lastDate format: ${parameters.lastDate}`);
    return false;
  }
  return currentDate <= lastDate;
};

const checkMonthlyDateRange = (parameters: any, currentDate: Date): boolean => {
  if (!parameters?.range || ((!parameters?.startRange || !parameters?.endRange) && !parameters?.dates && !parameters?.days)) {
    return true;
  }
  const { range, startRange, endRange, dates, days } = parameters;
  
  if (range === 'absolute') {
    return checkAbsoluteDateRange(startRange, endRange, currentDate);
  } else if (range === 'relative') {
    return checkRelativeDateRange(Number(startRange), Number(endRange), currentDate);
  } else if (range === 'list_of_dates') {
    if (!Array.isArray(dates) || dates.length === 0) return false;
    const currentISO = currentDate.toISOString().split('T')[0];
    return dates.includes(currentISO);
  } else if (range === 'list_of_days') {
    if (!Array.isArray(days) || days.length === 0) return false;
    const currentDay = currentDate.getDate();
    return days.includes(currentDay);
  }
  
  return true;
};

const checkAbsoluteDateRange = (startRange: string, endRange: string, currentDate: Date): boolean => {
  const startDate = new Date(startRange);
  const endDate = new Date(endRange);

  if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
    console.warn(`Invalid date range: ${startRange} to ${endRange}`);
    return false;
  }

  startDate.setHours(0, 0, 0, 0);
  endDate.setHours(0, 0, 0, 0);
  const currentDateOnly = new Date(currentDate);
  currentDateOnly.setHours(0, 0, 0, 0);

  return currentDateOnly >= startDate && currentDateOnly <= endDate;
};

const checkRelativeDateRange = (startDay: number, endDay: number, currentDate: Date): boolean => {
  const currentDay = currentDate.getDate();

  if (startDay <= endDay) {
    return currentDay >= startDay && currentDay <= endDay;
  }

  return currentDay >= startDay || currentDay <= endDay;
};

const checkDependentNudgeValidity = (parameters: any, nudges: ValidatableNudge[]): boolean =>{
  if (!parameters?.dependentNudges) {
    return true;
  }

  if (parameters?.dependentNudges.length > 0) {
    const availableCampaignEnums = nudges.map(nudge => nudge.campaignEnum);
    const allDependentNudgesExist = parameters.dependentNudges.every(
      (dependentNudge: string) => availableCampaignEnums.includes(dependentNudge)
    );
    return allDependentNudgesExist;
  }

  return true;
}